import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { promptTemplateApi } from "@/apis/promptTemplate";
import Router from "@/constants/router";
import { CreatePromptTemplateDto } from "@/dto/PromptTemplateDto";

interface FormData extends CreatePromptTemplateDto {}

export default function useCreatePromptTemplate() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      templateKey: "",
      templateName: "",
      templateDescription: "",
      templateCategory: "",
      systemPrompt: "",
      userPromptTemplate: "",
      assistantPrompt: "",
      promptVariables: null,
      aiModelProvider: "OPENAI",
      aiModelName: "gpt-4o",
      aiModelTemperature: 0.7,
      aiMaxTokens: 4000,
      aiModelConfig: null,
      useCondition: "",
      priorityOrder: 1,
    },
  });

  const handleSubmit = useCallback(async (data: FormData) => {
    setLoading(true);
    try {
      const createData: CreatePromptTemplateDto = {
        ...data,
        promptVariables: data.promptVariables ? JSON.parse(data.promptVariables) : null,
        aiModelConfig: data.aiModelConfig ? JSON.parse(data.aiModelConfig) : null,
      };

      await promptTemplateApi.create(createData);
      enqueueSnackbar("Prompt template created successfully.", { variant: "success" });
      navigate(Router.PromptTemplate);
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to create prompt template", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [navigate, enqueueSnackbar]);

  const handleCancel = useCallback(() => {
    navigate(Router.PromptTemplate);
  }, [navigate]);

  return {
    form,
    loading,
    handleSubmit,
    handleCancel,
  };
}